import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import Sidebar from "../components/Sidebar";
import { COLORS } from "../assets/theme";
import { SA } from "../components/superadmin/saUi";
import {
  getCachedSuperadmin,
  getSuperadminMe,
  superadminLogout,
  SuperadminAdmin,
} from "../services/superadminService";

const NAV_ITEMS = [
  { label: "Overview", icon: <SpaceDashboardOutlinedIcon />, to: "/superadmin/overview" },
  { label: "Organizations", icon: <ApartmentOutlinedIcon />, to: "/superadmin/organizations" },
  { label: "Packages", icon: <WorkspacePremiumOutlinedIcon />, to: "/superadmin/plans" },
  { label: "Payments", icon: <PaymentsOutlinedIcon />, to: "/superadmin/payments" },
  { label: "Messages", icon: <ForumOutlinedIcon />, to: "/superadmin/messages" },
  { label: "Message prices", icon: <SellOutlinedIcon />, to: "/superadmin/pricing" },
  { label: "Features", icon: <ExtensionOutlinedIcon />, to: "/superadmin/features" },
  { label: "Settings", icon: <SettingsOutlinedIcon />, to: "/superadmin/settings" },
];

const DRAWER_WIDTH = 220;
const COLLAPSED_WIDTH = 70;
const COLLAPSED_KEY = "superadmin.sidebarCollapsed";

/** The platform panel: the dashboard's sidebar, with its own top bar. */
const SuperadminLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [admin, setAdmin] = useState<SuperadminAdmin | null>(getCachedSuperadmin());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    getSuperadminMe()
      .then(setAdmin)
      .catch(() => {
        // A 401 is already handled by the panel's interceptor.
      });
  }, []);

  // The phone drawer closes once a page is picked.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Storage unavailable: the choice just won't persist.
    }
  }, [collapsed]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: COLORS.MainBackground, overflowX: "hidden" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "rgba(255, 255, 255, 0.86)",
          backdropFilter: "saturate(180%) blur(10px)",
          color: SA.text,
          borderBottom: `1px solid ${SA.border}`,
        }}
      >
        <Toolbar sx={{ gap: 1, px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            aria-label="Open menu"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <ButtonBase
            onClick={() => navigate("/superadmin/overview")}
            aria-label="Platform overview"
            sx={{ display: "flex", alignItems: "center", gap: 1, borderRadius: 2, px: 0.75, py: 0.5 }}
          >
            <Box
              aria-hidden
              sx={{
                width: 32,
                height: 32,
                borderRadius: "9px",
                display: "grid",
                placeItems: "center",
                bgcolor: SA.text,
                color: "#fff",
                "& svg": { fontSize: 18 },
              }}
            >
              <ShieldOutlinedIcon />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.02rem", color: SA.text }}>Platform</Typography>
          </ButtonBase>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            onClick={(e) => setAnchorEl(e.currentTarget)}
            startIcon={<AccountCircleIcon />}
            sx={{ color: SA.text, textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {isSmUp && (admin?.fullName || admin?.phone || "Superadmin")}
          </Button>
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled sx={{ opacity: "1 !important" }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{admin?.fullName || "Superadmin"}</Typography>
                <Typography sx={{ fontSize: "0.8rem", color: COLORS.placeHolderColor }}>{admin?.phone || ""}</Typography>
              </Box>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                superadminLogout();
              }}
            >
              Log out everywhere
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Sidebar
        mobileOpen={mobileOpen}
        onDrawerToggle={() => setMobileOpen((open) => !open)}
        sidebarCollapsed={collapsed}
        onSidebarToggle={() => setCollapsed((value) => !value)}
        drawerWidth={DRAWER_WIDTH}
        collapsedWidth={COLLAPSED_WIDTH}
        menuItems={NAV_ITEMS}
        venueName="Admin"
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: { xs: 7, sm: 8 },
          px: { xs: 2, sm: 3, lg: 4 },
          py: { xs: 2, sm: 3 },
          boxSizing: "border-box",
          width: isSmUp ? `calc(100% - ${collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH}px)` : "100%",
          minWidth: 0,
          transition: theme.transitions.create("width", { duration: theme.transitions.duration.short }),
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default SuperadminLayout;
